import { useEffect, useState, useRef } from "react";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

export default function AssociatedClients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Create State
    const [showCreate, setShowCreate] = useState(false);
    const [newClient, setNewClient] = useState({
        clientName: "",
        websiteUrl: "",
        isActive: true,
    });
    const [createImage, setCreateImage] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const fileInputRef = useRef(null);

    // Edit State
    const [showEdit, setShowEdit] = useState(false);
    const [editClient, setEditClient] = useState(null);
    const [editImage, setEditImage] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const editFileInputRef = useRef(null);

    // Fetch Clients
    const fetchClients = async () => {
        setLoading(true);
        try {
            const res = await makeRequestAuth("post", "/admin/getAssociatedClients", {
                sortOrder: "desc",
                sortBy: "createdAt"
            });

            if (res?.error) {
                setClients([]);
                return showErrorToast(res.message || "Failed to fetch clients");
            }
            
            // Optional: Filter locally based on search if backend doesn't handle keyword search for clients yet
            let fetchedClients = res?.data?.list || [];
            if (search.trim()) {
                fetchedClients = fetchedClients.filter(c => 
                    c.clientName.toLowerCase().includes(search.toLowerCase())
                );
            }
            setClients(fetchedClients);
        } catch (error) {
            showErrorToast("Something went wrong while fetching clients");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, [search]); // re-run filter/fetch if search changes

    // Create Client
    const handleCreate = async () => {

        if (!createImage) {
            return showErrorToast("Client logo is required");
        }

        setIsCreating(true);
        try {
            // 1. Upload Image
            const uploadRes = await uploadFiles({
                files: [createImage],
                endpoint: "/uploadImage",
            });

            if (uploadRes?.error || !uploadRes?.data?.imageUrl) {
                setIsCreating(false);
                return showErrorToast("Image upload failed");
            }

            const imageUrl = uploadRes.data.imageUrl;

            // 2. Create Client
            const res = await makeRequestAuth("post", "/admin/createAssociatedClient", {
                clientName: newClient.clientName,
                websiteUrl: newClient.websiteUrl,
                clientLogo: imageUrl,
                isActive: newClient.isActive
            });

            if (res?.error) {
                setIsCreating(false);
                return showErrorToast(res.message || "Failed to create client");
            }

            showSuccessToast("Client added successfully");
            setShowCreate(false);
            setNewClient({ clientName: "", websiteUrl: "", isActive: true });
            setCreateImage(null);
            fetchClients();
        } catch (error) {
            showErrorToast(error.message || "Failed to create client");
        } finally {
            setIsCreating(false);
        }
    };

    // Update Client
    const handleUpdate = async () => {


        setIsUpdating(true);
        try {
            let imageUrl = editClient.clientLogo;

            // 1. Upload Image if new one selected
            if (editImage) {
                const uploadRes = await uploadFiles({
                    files: [editImage],
                    endpoint: "/uploadImage",
                });
                if (uploadRes?.error || !uploadRes?.data?.imageUrl) {
                    setIsUpdating(false);
                    return showErrorToast("Image upload failed");
                }
                imageUrl = uploadRes.data.imageUrl;
            }

            // 2. Update Client
            const res = await makeRequestAuth("post", "/admin/updateAssociatedClient", {
                id: editClient._id,
                clientName: editClient.clientName,
                websiteUrl: editClient.websiteUrl,
                clientLogo: imageUrl,
                isActive: editClient.isActive
            });

            if (res?.error) {
                setIsUpdating(false);
                return showErrorToast(res.message || "Failed to update client");
            }

            showSuccessToast("Client updated successfully");
            setShowEdit(false);
            setEditClient(null);
            setEditImage(null);
            fetchClients();
        } catch (error) {
            showErrorToast(error.message || "Failed to update client");
        } finally {
            setIsUpdating(false);
        }
    };

    // Delete Client
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this client?")) return;
        
        try {
            const res = await makeRequestAuth("post", "/admin/deleteAssociatedClient", { id });
            if (res?.error) {
                return showErrorToast(res.message || "Failed to delete client");
            }
            showSuccessToast("Client deleted successfully");
            fetchClients();
        } catch (error) {
            showErrorToast("Failed to delete client");
        }
    };

    return (
        <div className="p-6">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
                <ToastContainer />
                <h2 className="text-2xl font-semibold text-[#434646]">
                    Associated Clients
                </h2>

                <button
                    onClick={() => {
                        setShowCreate(true);
                        setNewClient({ clientName: "", websiteUrl: "", isActive: true });
                        setCreateImage(null);
                    }}
                    className="px-4 py-2 rounded-xl bg-[#24ADD7] text-white hover:opacity-90"
                >
                    + Add Client
                </button>
            </div>

            {/* SEARCH */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search client by name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border px-4 py-2 rounded-xl w-full sm:w-[300px] outline-none focus:border-[#24ADD7]"
                />
            </div>

            {/* LOADING */}
            {loading && (
                <div className="text-center py-20 text-gray-500">
                    Loading clients...
                </div>
            )}

            {/* NO DATA */}
            {!loading && clients.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                    No clients found
                </div>
            )}

            {/* CLIENT CARDS */}
            {!loading && clients.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {clients.map((client) => (
                        <div key={client._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col items-center hover:shadow-md transition-shadow">
                            <img 
                                src={client.clientLogo} 
                                alt={client.clientName} 
                                className="w-24 h-24 object-contain mb-4 bg-gray-50 rounded-full border border-gray-100 p-2"
                            />
                            <h3 className="text-lg font-semibold text-center w-full truncate mb-1 text-gray-800" title={client.clientName}>
                                {client.clientName}
                            </h3>
                            <a 
                                href={client.websiteUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="text-sm text-[#24ADD7] truncate w-full text-center mb-4 hover:underline"
                            >
                                {client.websiteUrl || "No website link"}
                            </a>
                            
                            <div className="w-full flex justify-between items-center mt-auto pt-4 border-t border-gray-100">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${client.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {client.isActive ? "Active" : "Inactive"}
                                </span>
                                <div className="flex gap-3">
                                    <button 
                                        onClick={() => {
                                            setEditClient(client);
                                            setEditImage(null);
                                            setShowEdit(true);
                                        }}
                                        className="text-gray-500 hover:text-[#24ADD7] font-medium text-sm transition-colors"
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(client._id)}
                                        className="text-red-400 hover:text-red-600 font-medium text-sm transition-colors"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* CREATE MODAL */}
            {showCreate && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
                        <h3 className="text-xl font-semibold mb-5 text-[#434646]">Add New Client</h3>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Client Name (Optional)</label>
                            <input
                                type="text"
                                placeholder="Enter client name"
                                value={newClient.clientName}
                                onChange={(e) => setNewClient({ ...newClient, clientName: e.target.value })}
                                className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#24ADD7]"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Website URL (Optional)</label>
                            <input
                                type="text"
                                placeholder="https://example.com"
                                value={newClient.websiteUrl}
                                onChange={(e) => setNewClient({ ...newClient, websiteUrl: e.target.value })}
                                className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#24ADD7]"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Client Logo <span className="text-red-500">*</span></label>
                            <div className="w-full border border-gray-300 px-4 py-2 rounded-xl flex items-center bg-gray-50 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    onChange={(e) => setCreateImage(e.target.files[0])}
                                    className="hidden"
                                />
                                <span className="text-sm text-gray-500">{createImage ? createImage.name : "Choose an image..."}</span>
                            </div>
                        </div>

                        <div className="mb-6 flex items-center gap-2 cursor-pointer" onClick={() => setNewClient({ ...newClient, isActive: !newClient.isActive })}>
                            <input
                                type="checkbox"
                                checked={newClient.isActive}
                                readOnly
                                className="w-4 h-4 text-[#24ADD7] accent-[#24ADD7] cursor-pointer"
                            />
                            <label className="text-gray-700 cursor-pointer select-none">Display on Homepage (Active)</label>
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => {
                                    setShowCreate(false);
                                    setNewClient({ clientName: "", websiteUrl: "", isActive: true });
                                    setCreateImage(null);
                                }}
                                className="px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-700 font-medium transition-colors"
                                disabled={isCreating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={isCreating}
                                className="px-5 py-2 bg-[#24ADD7] text-white rounded-xl disabled:bg-gray-400 hover:opacity-90 font-medium transition-colors flex items-center"
                            >
                                {isCreating ? "Saving..." : "Create Client"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {showEdit && editClient && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
                        <h3 className="text-xl font-semibold mb-5 text-[#434646]">Edit Client</h3>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Client Name (Optional)</label>
                            <input
                                type="text"
                                placeholder="Enter client name"
                                value={editClient.clientName}
                                onChange={(e) => setEditClient({ ...editClient, clientName: e.target.value })}
                                className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#24ADD7]"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Website URL (Optional)</label>
                            <input
                                type="text"
                                placeholder="https://example.com"
                                value={editClient.websiteUrl || ""}
                                onChange={(e) => setEditClient({ ...editClient, websiteUrl: e.target.value })}
                                className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#24ADD7]"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-1">Update Logo (Optional)</label>
                            <div className="w-full border border-gray-300 px-4 py-2 rounded-xl flex items-center bg-gray-50 cursor-pointer justify-between" onClick={() => editFileInputRef.current?.click()}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={editFileInputRef}
                                    onChange={(e) => setEditImage(e.target.files[0])}
                                    className="hidden"
                                />
                                <span className="text-sm text-gray-500">{editImage ? editImage.name : "Choose a new image..."}</span>
                            </div>
                             {!editImage && (
                                <div className="mt-3 flex items-center gap-3">
                                    <span className="text-xs text-gray-500 font-medium">Current Logo:</span>
                                    <img src={editClient.clientLogo} alt="current logo" className="h-10 w-10 object-contain rounded-full border border-gray-200 p-1" />
                                </div>
                            )}
                        </div>

                        <div className="mb-6 flex items-center gap-2 cursor-pointer" onClick={() => setEditClient({ ...editClient, isActive: !editClient.isActive })}>
                            <input
                                type="checkbox"
                                checked={editClient.isActive}
                                readOnly
                                className="w-4 h-4 text-[#24ADD7] accent-[#24ADD7] cursor-pointer"
                            />
                            <label className="text-gray-700 cursor-pointer select-none">Display on Homepage (Active)</label>
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                onClick={() => {
                                    setShowEdit(false);
                                    setEditClient(null);
                                    setEditImage(null);
                                }}
                                className="px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-700 font-medium transition-colors"
                                disabled={isUpdating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={isUpdating}
                                className="px-5 py-2 bg-[#24ADD7] text-white rounded-xl disabled:bg-gray-400 hover:opacity-90 font-medium transition-colors"
                            >
                                {isUpdating ? "Updating..." : "Update Client"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
